import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { appStore } from '../../app/AppStore';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { IUserSettings } from '../models/IUserSettings';
import { Module } from '../Module';
import { Languages } from '../resources/Languages';
import { Locales } from '../resources/Locales';
import { UserSettings } from '../resources/UserSettings';
import { ILanguage } from '../ServerApi/types/ILanguage';
import { ILocale } from '../ServerApi/types/ILocale';
import { ServerService } from '../services/ServerService';
import { ChangeLanguageNotification, ChangeLocaleNotification } from '../services/UserSettingsNotification';

export interface IEditLanguageAndLocaleCtrls {
  save(): void;
  canSave(): void;
  isBusy(): void;
}

export interface IEditLanguageAndLocaleScope extends ng.IScope {
  controls: IEditLanguageAndLocaleCtrls;
  serverUser: IServerUserInfo;
  userSettings: IUserSettings;
  onSaveComplete(): any;

  language: string;
  locale: string;

  languages: ILanguage[];
  locales: ILocale[];

  model: { language: string, locale: string };
}

Module.instance().directive('tbEditLanguageAndLocale', <any>[
  '$q',
  'ChangeLanguageNotification',
  'ChangeLocaleNotification',
  'Languages',
  'Locales',
  'UserSettings',
  'ServerService',
  (
    $q: ng.IQService,
    changeLanguageNotification: ChangeLanguageNotification,
    changeLocaleNotification: ChangeLocaleNotification,
    languages: Languages,
    locales: Locales,
    userSettings: UserSettings,
    serverService: ServerService) => {
    return {
      restrict: 'E',
      replace: 'true',
      templateUrl: 'editLanguageAndLocale.html',
      scope: {
        controls: '=?',
        serverUser: '=',
        language: '=?',
        locale: '=?'
      },
      link: (scope: IEditLanguageAndLocaleScope) => {
        // these are shown as 'Unspecified' in the UI, and tell the server to use the server language/locale for the user.
        const unspecifiedLanguage = languages.unspecifiedLanguage();
        const unspecifiedLocale = locales.unspecifiedLocale();

        // the server returns no language/locale for users that haven't explicitly set one.
        // we need to map that to the special client-only value 'Unspecified' in the UI.
        const applyUnspecifiedLanguageIfNecessary = () => {
          if (!scope.language) {
            scope.language = unspecifiedLanguage.language;
          }
        };
        const applyUnspecifiedLocaleIfNecessary = () => {
          if (!scope.locale) {
            scope.locale = unspecifiedLocale.locale;
          }
        };

        applyUnspecifiedLanguageIfNecessary();
        applyUnspecifiedLocaleIfNecessary();
        scope.$watch('language', () => {
          applyUnspecifiedLanguageIfNecessary();
        });
        scope.$watch('locale', () => {
          applyUnspecifiedLocaleIfNecessary();
        });

        scope.model = {
          language: scope.language,
          locale: scope.locale
        };

        languages.getAll().then((langs: ILanguage[]) => {
          // add unspecified to the list to allow the user to unset their language.
          langs.unshift(unspecifiedLanguage);
          scope.languages = langs;
        });

        locales.getAll().then((locs: ILocale[]) => {
          // add unspecified to the list to allow the user to unset their locale
          locs.unshift(unspecifiedLocale);
          scope.locales = locs;
        });

        let isBusy = false;
        const isDirty = () => {
          return isLanguageDirty() || isLocaleDirty();
        };
        const isLanguageDirty = () => scope.model.language !== scope.language;
        const isLocaleDirty = () => scope.model.locale !== scope.locale;
        const userIsViewingTheirOwnSettings = () => {
          return serverService.sessionUsername() === scope.serverUser.username
            && serverService.sessionUserDomainName() === scope.serverUser.domainName;
        };

        const maybeShowChangeLanguageNotification = (maybeUpdatedLanguagePromise: ng.IPromise<any>) => {
          maybeUpdatedLanguagePromise.then((result) => {
            if (result) {
              changeLanguageNotification.notify(result, scope.serverUser.username);
            }
          });
        };
        const maybeShowChangeLocaleNotification = (maybeUpdatedLocalePromise: ng.IPromise<any>) => {
          maybeUpdatedLocalePromise.then((result) => {
            if (result) {
              changeLocaleNotification.notify(result, scope.serverUser.username);
            }
          });
        };

        scope.controls = {
          save: () => {
            isBusy = true;
            const maybeUpdateLanguage = isLanguageDirty() ?
                                          userSettings.updateUserLanguage(scope.serverUser.id, scope.model.language) :
                                          $q.when(false);
            const maybeUpdateLocale = isLocaleDirty() ?
                                        userSettings.updateUserLocale(scope.serverUser.id, scope.model.locale) :
                                        $q.when(false);
            $q.all([maybeUpdateLanguage, maybeUpdateLocale]).then(() => {

              // save completed successfully, so update the language and locale on the scope.
              scope.language = scope.model.language;
              scope.locale = scope.model.locale;
              isBusy = false;
              if (userIsViewingTheirOwnSettings()) {
                serverService.refreshSessionInfo().then(() => {
                  appStore.dispatch(incrementRouterSpaceReactKey());

                  // wait to show toast so that they are rendered in the new language
                  maybeShowChangeLanguageNotification(maybeUpdateLanguage);
                  maybeShowChangeLocaleNotification(maybeUpdateLocale);
                });
              } else {
                // show toast right away since we are changing for another user
                maybeShowChangeLanguageNotification(maybeUpdateLanguage);
                maybeShowChangeLocaleNotification(maybeUpdateLocale);
              }
            });
          },
          canSave: () => isDirty() && !isBusy,
          isBusy: () => isBusy
        };
      }
    };
  }
]);
